import { useMemo, useState } from "react";
import { ArrowLeft, Check, Loader2, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";

import { Money } from "@workspace/accounting/components";
import { CheckOperation } from "@workspace/operations/check-operation";
import { checkOperation } from "@workspace/operations/checkOperation";
import type { ProductIngredient } from "@workspace/product-catalog-types/types";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@workspace/ui/components/alert-dialog";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { ButtonGroup } from "@workspace/ui/components/button-group";
import { CardFooter } from "@workspace/ui/components/card";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import { Separator } from "@workspace/ui/components/separator";
import { cn } from "@workspace/ui/lib/utils";
import { OrderPrintStatePanel } from "@workspace/venue-management/printing";

import type { OrderItem, SelectedOptionGroup, TabInput } from "../../types";
import {
	compedItemIds,
	compReasonsByItem,
	compRefusalMessage,
	isCompletedByComps,
} from "../comp";
import { useEposContext } from "../epos-context";
import { canChangeOrderMoney } from "../money-change";
import { optionRequiredOf } from "../option-required";
import {
	MANAGE_TABS_OPERATION,
	SENT_ITEM_REMOVE_FORBIDDEN_MESSAGE,
} from "../removal";
import { describeResendWarning, RESEND_SUCCESS } from "../resend-warnings";
import { SEND_SUCCESS, shouldShowSendSuccess } from "../send-warnings";
import {
	isRejected,
	needsReturnPrompt,
	nothingWasRefused,
	RETURN_PROMPT_BODY,
	RETURN_PROMPT_TITLE,
	type RejectedStockLine,
} from "../stock-warnings";
import { AddOrderToTabDialog } from "./add-order-to-tab-dialog";
import { CloseOrderDialog } from "./close-order-dialog";
import { CompDialog } from "./comp-dialog";
import { CourseGroupedOrderItems } from "./course-grouped-order-items";
import { LineSyncBadge } from "./line-sync-badge";
import { MoveOrderToTabDialog } from "./move-order-to-tab-dialog";
import { OptionsSummary } from "./options-summary";
import { OrderCompControl } from "./order-comp-control";
import { OrderItemOptionsEditor } from "./order-item-options-editor";
import { prepTimeLabel } from "./prep-time";
import { PrintReceiptButton } from "./print-receipt-button";
import { SendWarningDialog } from "./send-warning-dialog";
import { UnsentItemsDialog } from "./unsent-items-dialog";

export function OpenOrderScreen() {
	const {
		data,

		setView,
		setActiveOrderId,

		openOrderData,

		tabs,
		activeTabId,
		setActiveTabId,
		setTipAmount,
		setTipPercentage,
		setSplitMethod,

		setSelectedCategory,
		currentOrder,
		setRefundOrder,
		setIsRefundDialogOpen,

		currencies: { formatter },
		removeItem: removeItemFromOrder,
		restoreRemoved,
		restoreStatus,
		updateItemOptions,
		compItem,
		removeItemComp,
		compOrder,
		removeOrderComp,
		completeOrder: _completeOrder,
		updateOrder,

		getAvailableOptions,
		products,

		operations,
		tenant,
		venueId,

		assignOrderToTab,
		moveOrderToTab,
		isMovingOrder,

		createTab,

		closeOrder,
		dismissOrder,
		sendToKitchen,
		saveOrder,
		moveItemToCourse,
		resendTickets,
		courseFire,
		sendWarnings,
		lineSyncStatus,
		orderSyncState,
		retryOrderWrites,
		setServiceChargeRemoved,
		optionRefusal,
		showOptionRefusal,
		clearOptionRefusal,
	} = useEposContext();

	const [isChangingServiceCharge, setIsChangingServiceCharge] = useState(false);
	// The row shows when the order carries a charge, or has had one taken off, or the server's figures say there is one. A
	// new order's copy on the till has no snapshot until it's reloaded, but its first line write answers with the charge.
	const showServiceCharge =
		openOrderData?.serviceCharge?.enabled ||
		openOrderData?.serviceChargeRemoved ||
		(openOrderData?.serviceChargeAmount?.value ?? 0) > 0;
	// HA-357 slice C (AC9) — takes the charge off, or puts it back, at the customer's request; the server's answer is the total.
	const toggleServiceCharge = async () => {
		if (!openOrderData) return;
		setIsChangingServiceCharge(true);
		try {
			await setServiceChargeRemoved(
				openOrderData.id,
				!openOrderData.serviceChargeRemoved,
			);
		} catch (error) {
			// The planning ruling: part of the charge already paid, so it can't come off without a refund (HA-789's).
			const code = (error as { problem?: { code?: string } }).problem?.code;
			toast.error(
				code === "SERVICE_CHARGE_EXCEEDS_OUTSTANDING"
					? "Part of the service charge is already paid. A refund is needed to take it off."
					: "Couldn't change the service charge",
			);
		} finally {
			setIsChangingServiceCharge(false);
		}
	};

	const [editingItem, setEditingItem] = useState<OrderItem | null>(null);
	// HA-658 — the line whose Comp reason prompt is open.
	const [compTarget, setCompTarget] = useState<OrderItem | null>(null);
	const [isOptionsEditorOpen, setIsOptionsEditorOpen] = useState(false);
	const [isAddToTabOpen, setIsAddToTabOpen] = useState(false);
	/** HA-588 — the line waiting on "Returned to stock?", if any. */
	const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);
	/** HA-588 — lines the last send refused on stock grounds. The rest of the order went. */
	const [stockRejections, setStockRejections] = useState<RejectedStockLine[]>(
		[],
	);
	const [isMoveToTabOpen, setIsMoveToTabOpen] = useState(false);
	const [selectedTabIdForOrder, setSelectedTabIdForOrder] = useState<
		string | null
	>(null);
	const [isCreatingTabForOrder, setIsCreatingTabForOrder] = useState(false);
	const [isCloseOrderOpen, setIsCloseOrderOpen] = useState(false);
	const [isDismissConfirmOpen, setIsDismissConfirmOpen] = useState(false);
	const [isDismissing, setIsDismissing] = useState(false);
	/** HA-767 — Complete is waiting on the server. */
	const [isCompleting, setIsCompleting] = useState(false);
	/** HA-768 / D-HA-24 — Save is waiting on the server. */
	const [isSaving, setIsSaving] = useState(false);
	const [isUnsentItemsDialogOpen, setIsUnsentItemsDialogOpen] = useState(false);
	const [pendingAction, setPendingAction] = useState<"pay" | "close" | null>(
		null,
	);

	// HA-1024 — the line a refused send, save or payment is waiting on, while it is on this order: its editor is open
	// with the refusal inside it, over any other line's.
	const refusedLine =
		optionRefusal?.orderId === openOrderData?.id
			? openOrderData?.items.find((i) => i.id === optionRefusal?.lineId)
			: undefined;
	const editorItem = refusedLine ?? editingItem;

	// Compute move-to-course targets for the editing item (AC4, AC11, AC12)
	const moveTargets = useMemo(() => {
		if (!openOrderData || !editorItem) return [];

		const courses = data.settings.courses;
		// Unfired standard courses: not auto-fire, and no item in the course has been sent
		return courses
			.filter((c) => {
				if (c.autoFire) return false;
				if (c.id === editorItem.courseId) return false;
				// A course is "fired" if any item in it has sentToKitchen
				const courseItems = openOrderData.items.filter(
					(i) => i.courseId === c.id,
				);
				const isFired = courseItems.some((i) => i.sentToKitchen);
				return !isFired;
			})
			.sort((a, b) => a.sortOrder - b.sortOrder);
	}, [openOrderData, editorItem, data.settings.courses]);

	const handleMoveToCourse = (itemId: string, toCourseId: string) => {
		if (!openOrderData) return;
		moveItemToCourse(openOrderData.id, itemId, toCourseId);
	};

	if (!openOrderData) return null;

	/**
	 * HA-588 — removing a line that was already SENT has to ask whether the food came back, because
	 * only the operator knows and the stock answer differs either way. An unsent line deducted
	 * nothing, so it is removed without a question.
	 */
	const canRemoveSent = checkOperation({
		operation: MANAGE_TABS_OPERATION,
		operations,
	});

	/**
	 * HA-658 slice 4 — Comp and Remove Comp are server-confirmed: each says what the server refused (a toast for each 409) and
	 * rethrows, so the reason prompt stays open with its input. The controls are gated on `staff-terminal-comp` (the route's own key).
	 */
	const mayComp = checkOperation({
		operation: "staff-terminal-comp",
		operations,
	});

	const tellRefusal = async <T,>(write: Promise<T>) => {
		try {
			return await write;
		} catch (error) {
			toast.error(compRefusalMessage(error));
			throw error;
		}
	};
	const compLine = (reason: string) =>
		compTarget
			? tellRefusal(compItem(openOrderData.id, compTarget.id, reason))
			: Promise.resolve();
	const removeLineComp = (itemId: string) =>
		tellRefusal(removeItemComp(openOrderData.id, itemId)).catch(() => {});
	const canTakeComp = mayComp && canChangeOrderMoney(openOrderData);
	const canUndoComp =
		mayComp &&
		(canChangeOrderMoney(openOrderData) || isCompletedByComps(openOrderData));

	const removeItem = async (itemId: string) => {
		const item = openOrderData.items.find((i) => i.id === itemId);

		// HA-265 (AC3): removing a SENT line needs `epos:tabs:manage`. Say so before the prompt and send nothing; the server
		// refuses too.
		if (item && needsReturnPrompt(item) && !canRemoveSent) {
			toast.error(SENT_ITEM_REMOVE_FORBIDDEN_MESSAGE);
			return;
		}

		if (item && needsReturnPrompt(item)) {
			setPendingRemoval(itemId);
			return;
		}

		await removeItemFromOrder(openOrderData.id, itemId);
	};

	// HA-768 — a failed remove has already said so (the line shows Not removed, with a Retry).
	const removeLine = (itemId: string) => {
		void removeItem(itemId).catch(() => {});
	};

	const confirmRemoval = async (returnedToStock: boolean) => {
		const itemId = pendingRemoval;
		setPendingRemoval(null);

		if (itemId) {
			await removeItemFromOrder(
				openOrderData.id,
				itemId,
				returnedToStock,
			).catch(() => {});
		}
	};

	/**
	 * HA-265 (AC7) — Restore last removed item, while the server has one to give back. A sent line's restore needs the same
	 * permission as removing it. The line returns once the server has put it back.
	 */
	const restoreState = restoreStatus(openOrderData.id);
	const restoreLast = () => {
		const last = openOrderData.lastRemoved;
		if (!last) return;

		if (last.wasSent && !canRemoveSent) {
			toast.error(SENT_ITEM_REMOVE_FORBIDDEN_MESSAGE);
			return;
		}

		void restoreRemoved(openOrderData.id, last.removalId).catch(() => {});
	};

	/** HA-768 / D-HA-24 — this order's writes the server hasn't confirmed. */
	const sync = orderSyncState(openOrderData.id);
	const statusOfLine = (itemId: string) =>
		lineSyncStatus(openOrderData.id, itemId);
	const retryLines = () => {
		void retryOrderWrites(openOrderData.id).catch(() => {});
	};

	const handleEditItem = (item: OrderItem) => {
		setEditingItem(item);
		setIsOptionsEditorOpen(true);
	};

	// HA-877: the server refuses a send, a payment or a complete while a line has an unanswered required option
	// group. This is where the operator is taken to it: the line's editor opens with what is missing, in the
	// server's words, inside it (HA-1024: a toast sat over Save Changes). Any other failure is shown as before.
	const reportRefusal = (error: unknown, title: string, fallback?: string) => {
		const missing = optionRequiredOf(error);
		if (missing) {
			showOptionRefusal(openOrderData.id, missing);
			return;
		}

		toast.error(title, {
			description: error instanceof Error ? error.message : fallback,
		});
	};

	// HA-768 / D-HA-24 — the editor closes (through onClose) once the server has the change, and
	// stays open with the input kept if it fails.
	const handleSaveOptions = (
		itemId: string,
		selectedOptions: SelectedOptionGroup[],
		customNote: string,
		removedIngredients: ProductIngredient[],
	) =>
		updateItemOptions(
			openOrderData.id,
			itemId,
			selectedOptions,
			customNote,
			removedIngredients,
		);

	const handleDeleteItem = (itemId: string) => removeItem(itemId);

	const handleCloseOptionsEditor = () => {
		setIsOptionsEditorOpen(false);
		setEditingItem(null);
		clearOptionRefusal();
	};

	// HA-767 — the order is Completed when the server says so: pending while it answers, and on
	// failure the order stays here with an error rather than vanishing as though it were done.
	const completeOrder = async () => {
		if (isCompleting) return;

		setIsCompleting(true);
		try {
			await _completeOrder();
		} catch (error) {
			reportRefusal(error, "Failed to complete order", "Unknown error");
		} finally {
			setIsCompleting(false);
		}
	};

	const proceedToPaymentScreen = () => {
		setTipAmount(0);
		setTipPercentage(null);
		setSplitMethod("none");
		setView("payment");
	};

	const goToPaymentScreen = () => {
		// HA-768 / D-HA-24 — nothing is paid for that the server doesn't have.
		if (sync.state === "failed" || sync.state === "paused") {
			toast.error("Some items haven't been saved yet", {
				description:
					sync.state === "paused"
						? "They'll be sent when the connection is back."
						: "Retry or remove them before taking payment.",
			});
			return;
		}

		if (hasUnsentItems) {
			setPendingAction("pay");
			setIsUnsentItemsDialogOpen(true);
			return;
		}
		proceedToPaymentScreen();
	};

	const handleCloseOrderClick = () => {
		if (hasUnsentItems) {
			setPendingAction("close");
			setIsUnsentItemsDialogOpen(true);
			return;
		}
		setIsCloseOrderOpen(true);
	};

	const handleUnsentItemsRemoved = (wasOrderEmptied: boolean) => {
		setIsUnsentItemsDialogOpen(false);
		const action = pendingAction;
		setPendingAction(null);

		if (wasOrderEmptied) {
			// Order auto-cancelled — navigate away
			setActiveOrderId(null);
			setView("products");
			toast.success("Order cancelled");
			return;
		}

		if (action === "pay") {
			proceedToPaymentScreen();
		} else if (action === "close") {
			setIsCloseOrderOpen(true);
		}
	};

	const goToStart = () => {
		setActiveOrderId(null);
		setSelectedCategory(null);
		setView("products");
	};

	const attachOrderToTab = async () => {
		if (!currentOrder) return;
		if (!selectedTabIdForOrder) return;

		const tab = tabs.find((t) => t.id === selectedTabIdForOrder);
		if (!tab) {
			toast.error("Tab not found");
			return;
		}

		// HA-554: a tab holds several orders, so any open tab takes this one (the server refuses a closed tab).
		try {
			await assignOrderToTab({
				orderId: currentOrder.id,
				tabId: tab.id,
			});

			const updatedOrder = {
				...currentOrder,
				tabId: tab.id,
				customer:
					currentOrder.customer ??
					(tab.name
						? {
								name: tab.name,
								phone: "",
							}
						: undefined),
				updatedAt: new Date().toISOString(),
			};

			// The tab's orders come from the server's answer (the mutation puts it in the EPOS cache).
			setActiveTabId(tab.id);
			updateOrder(updatedOrder);

			setIsAddToTabOpen(false);
			setSelectedTabIdForOrder(null);
		} catch (error) {
			// HA-1025-3 (74-4): a Tab refusal for a line's missing choice opens that line's editor, as Send does.
			if (optionRequiredOf(error)) {
				reportRefusal(error, "Failed to assign order to tab");
				return;
			}
			toast.error("Failed to assign order to tab", {
				description: error instanceof Error ? error.message : "Unknown error",
			});
		}
	};

	const createTabAndAttachOrder = async (tabInput: TabInput) => {
		if (!currentOrder) return;

		setIsCreatingTabForOrder(true);
		try {
			const result = await createTab(tabInput);
			if (!result.success) {
				toast.error("Failed to create tab");
				return;
			}

			try {
				await assignOrderToTab({
					orderId: currentOrder.id,
					tabId: tabInput.tabId!,
				});
			} catch (_error) {
				if (optionRequiredOf(_error)) {
					reportRefusal(_error, "Failed to assign order to tab");
					setIsAddToTabOpen(false);
					setSelectedTabIdForOrder(null);
					return;
				}
				toast.error("Tab created but failed to assign order", {
					description:
						"The tab has been created. You can assign the order manually.",
				});
				setIsAddToTabOpen(false);
				setSelectedTabIdForOrder(null);
				return;
			}

			const updatedOrder = {
				...currentOrder,
				tabId: tabInput.tabId!,
				customer:
					currentOrder.customer ??
					(tabInput.name ? { name: tabInput.name, phone: "" } : undefined),
				updatedAt: new Date().toISOString(),
			};

			setActiveTabId(tabInput.tabId!);
			updateOrder(updatedOrder);

			setIsAddToTabOpen(false);
			setSelectedTabIdForOrder(null);
		} catch (error) {
			toast.error("Failed to create tab", {
				description: error instanceof Error ? error.message : "Unknown error",
			});
		} finally {
			setIsCreatingTabForOrder(false);
		}
	};

	const handleMoveOrderToTab = async (toTabId: string) => {
		if (!currentOrder || !activeTabId) return;

		try {
			await moveOrderToTab({ orderId: currentOrder.id, toTabId });

			// Both tabs' orders come from the server's answer (the mutation puts it in the EPOS cache).
			setActiveTabId(toTabId);

			setIsMoveToTabOpen(false);
		} catch (error) {
			// HA-1025-3 (74-4): a move refused for a line's missing choice opens that line's editor, as Send does.
			if (optionRequiredOf(error)) {
				reportRefusal(error, "Failed to move order");
				return;
			}
			toast.error("Failed to move order", {
				description: error instanceof Error ? error.message : "Unknown error",
			});
		}
	};

	const formatCurrency = formatter.format;

	const isSplit = openOrderData.type === "Split";
	// HA-552 — order is locked while a split-bill payment is in progress.
	// Mirror the backend `Order.IsFrozen` rule client-side so disabled state
	// matches without waiting on a 409 round-trip.
	const isFrozen = !!openOrderData.isFrozen;

	const showStartPayment = openOrderData.status === "Open" && !isSplit;

	const hasUnsentItems =
		openOrderData.status === "Open" &&
		openOrderData.items.some((i) => !i.sentToKitchen && i.queueId);
	return (
		<>
			{sync.state === "paused" && (
				<div
					role="status"
					className="border-b border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm font-medium"
				>
					Offline — changes marked Not yet sent will be sent when the connection
					is back.
				</div>
			)}
			{isFrozen && (
				<div
					role="alert"
					className="border-b border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm font-medium"
				>
					Order locked — split-bill payment in progress.
				</div>
			)}
			<ScrollArea className="h-full overflow-hidden px-0 border-t">
				<CheckOperation
					operation="staff-terminal-view-course-status"
					operations={operations}
				>
					<CourseGroupedOrderItems
						items={openOrderData.items}
						courses={data.settings.courses}
						firedCourseIds={openOrderData.firedCourseIds}
						pendingCourseId={openOrderData.pendingCourseFire?.courseId}
						isEditable={openOrderData.status === "Open"}
						isSplit={isSplit}
						onEditItem={handleEditItem}
						onRemoveItem={removeLine}
						lineStatus={statusOfLine}
						onRetryLine={retryLines}
						compedItemIds={compedItemIds(openOrderData)}
						compReasons={compReasonsByItem(openOrderData)}
						onComp={canTakeComp ? setCompTarget : undefined}
						onRemoveComp={canUndoComp ? removeLineComp : undefined}
						onResend={async (courseId) => {
							if (!currentOrder) return;
							try {
								const result = await resendTickets(currentOrder.id, courseId);

								// HA-570 — a queue with no printer is skipped, not failed. Say so per
								// queue instead of reporting a success the operator will not see printed.
								// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- typed non-null, but a 200 with an empty body (the HA-570 failure) yields null; keep the guard
								if (result?.warnings?.length) {
									for (const warning of result.warnings) {
										toast.warning(describeResendWarning(warning), {
											duration: 10_000,
										});
									}
								}

								// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- typed non-null, but a 200 with an empty body (the HA-570 failure) yields null; keep the guard
								if (!result?.warnings?.length || result.queuesDispatched > 0) {
									toast.success(RESEND_SUCCESS);
								}
							} catch {
								toast.error("Failed to resend tickets");
							}
						}}
					/>
					{/* HA-575 — per-printer outcome + operator cancel/retry/reprint for this order. */}
					{currentOrder && (
						<div className="px-2 py-2">
							<OrderPrintStatePanel
								tenant={tenant}
								venueId={venueId}
								orderId={currentOrder.id}
								operations={operations}
							/>
						</div>
					)}
				</CheckOperation>
				<CheckOperation
					operation="staff-terminal-view-course-status"
					operations={operations}
					invert
				>
					{openOrderData.items.map((item) => {
						const hasOptions =
							item.selectedOptions && item.selectedOptions.length > 0;

						return (
							<div
								key={item.id}
								className={cn(
									"flex items-center justify-between border-b hover:bg-muted/50 transition-colors",
									{
										"items-start": item.notes || hasOptions,
									},
								)}
							>
								<button
									type="button"
									className="flex-1 items-start h-full text-left cursor-pointer py-4 pl-1"
									onClick={() => handleEditItem(item)}
									disabled={
										isSplit || isFrozen || openOrderData.status !== "Open"
									}
								>
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-1.5 font-medium max-w-[13.5rem] truncate">
											{item.sentToKitchen && (
												<Badge
													variant="secondary"
													className="text-[10px] px-1 py-0 shrink-0"
												>
													Sent
												</Badge>
											)}
											{item.name}
										</div>
										<span>
											<Money {...item.price} />
										</span>
									</div>
									{prepTimeLabel(item) && (
										<div className="mt-1 text-xs text-muted-foreground">
											{prepTimeLabel(item)}
										</div>
									)}
									{hasOptions && (
										<div className="mt-1 text-sm text-muted-foreground">
											<OptionsSummary
												selectedOptions={item.selectedOptions}
												removedIngredients={item.removedIngredients}
												className="text-xs"
											/>
										</div>
									)}
									{item.notes && (
										<Badge
											variant="outline"
											className="border-muted text-muted-foreground"
										>
											{item.notes}
										</Badge>
									)}
								</button>
								<LineSyncBadge
									status={statusOfLine(item.id)}
									onRetry={retryLines}
								/>
								{isRejected(stockRejections, item.id) && (
									<Badge
										variant="outline"
										className="border-destructive text-destructive mx-1"
									>
										Out of stock — not sent
									</Badge>
								)}
								{!isSplit && !isFrozen && openOrderData.status === "Open" && (
									<div className="flex gap-1 mx-1 my-2">
										<Button
											variant="outline"
											size="icon-xl"
											disabled={statusOfLine(item.id) === "removing"}
											onClick={() => removeLine(item.id)}
											aria-label="Remove item"
										>
											<X className="size-6" />
										</Button>
									</div>
								)}
							</div>
						);
					})}
				</CheckOperation>
			</ScrollArea>

			{!isSplit &&
				!isFrozen &&
				openOrderData.status === "Open" &&
				(openOrderData.lastRemoved || restoreState) && (
					<div className="flex items-center gap-2 border-b px-1 py-2">
						{openOrderData.lastRemoved && !restoreState && (
							<Button variant="outline" size="sm" onClick={restoreLast}>
								<RotateCcw className="size-3" />
								Restore last removed item
							</Button>
						)}
						<LineSyncBadge status={restoreState} onRetry={retryLines} />
					</div>
				)}

			<CompDialog
				open={!!compTarget}
				title={compTarget ? `Comp ${compTarget.name}` : "Comp"}
				onConfirm={compLine}
				onClose={() => setCompTarget(null)}
			/>
			{/* Options Editor Dialog */}
			{editorItem && (
				<OrderItemOptionsEditor
					key={editorItem.id}
					isOpen={isOptionsEditorOpen || !!refusedLine}
					onClose={handleCloseOptionsEditor}
					orderItem={editorItem}
					availableOptions={getAvailableOptions(editorItem)}
					onSave={handleSaveOptions}
					onDelete={handleDeleteItem}
					onMoveToCourse={handleMoveToCourse}
					moveTargets={moveTargets}
					product={products.find((p) => p.id === editorItem.productId)!}
					refusal={refusedLine ? optionRefusal?.detail : undefined}
				/>
			)}
			<CardFooter className="flex flex-col border-t">
				<div className="w-full space-y-2">
					<div className="flex items-center justify-between text-sm">
						<span>Subtotal</span>
						<span>
							<Money {...openOrderData.subtotal} />
						</span>
					</div>
					{data.settings.taxRate > 0 && (
						<div className="flex items-center justify-between text-sm">
							<span>Tax ({data.settings.taxRate}%)</span>
							<span>{formatCurrency(openOrderData.tax)}</span>
						</div>
					)}
					{showServiceCharge && (
						<>
							{/* HA-357 — the charge is its own line, never tax: it's not VAT, so it's not in the Tax row. */}
							<div className="flex items-center justify-between text-sm">
								<span>Service charge</span>
								<span>
									<Money
										{...(openOrderData.serviceChargeAmount ?? {
											...openOrderData.total,
											value: 0,
										})}
									/>
								</span>
							</div>
							<CheckOperation
								operation="staff-terminal-manage-tabs"
								operations={operations}
							>
								<div className="flex justify-end">
									<Button
										variant="ghost"
										size="sm"
										disabled={isChangingServiceCharge}
										onClick={toggleServiceCharge}
									>
										{openOrderData.serviceChargeRemoved
											? "Add service charge"
											: "Remove service charge"}
									</Button>
								</div>
							</CheckOperation>
						</>
					)}
					<Separator />
					<div className="flex items-center justify-between font-medium">
						<span>Total</span>
						<span>
							<Money {...openOrderData.total} />
						</span>
					</div>
				</div>
				<CheckOperation
					operation="staff-terminal-print-receipt"
					operations={operations}
				>
					<PrintReceiptButton
						orderId={openOrderData.id}
						label="Print Bill"
						variant="outline"
						className="mt-2 w-full"
					/>
				</CheckOperation>
				<OrderCompControl
					order={openOrderData}
					operations={operations}
					onComp={(reason) => tellRefusal(compOrder(openOrderData.id, reason))}
					onRemove={() => tellRefusal(removeOrderComp(openOrderData.id))}
				/>
				{showStartPayment && (
					<ButtonGroup className="w-full">
						<Button
							variant="default"
							size="lg"
							className="mt-4 h-16 grow"
							onClick={goToPaymentScreen}
							disabled={openOrderData.items.length === 0}
						>
							Payment
						</Button>
						<CheckOperation
							operation="staff-terminal-access-tabs"
							operations={operations}
						>
							<CheckOperation
								operation="staff-terminal-create-tabs"
								operations={operations}
							>
								<Button
									variant="secondary"
									size="lg"
									className={cn("mt-4 h-16 grow", {
										hidden: !!activeTabId,
									})}
									onClick={() => {
										setSelectedTabIdForOrder(null);
										setIsAddToTabOpen(true);
									}}
									disabled={openOrderData.items.length === 0}
								>
									Add to Tab
								</Button>
							</CheckOperation>
							{activeTabId && (
								<Button
									variant="secondary"
									size="lg"
									className={cn("mt-4 h-16 grow", {
										hidden: !activeTabId,
									})}
									onClick={() => {
										setActiveOrderId(null);
										setActiveTabId(null);
										setView("products");
										setSelectedCategory(null);
									}}
								>
									Leave Tab
								</Button>
							)}
							<CheckOperation
								operation="staff-terminal-move-order-between-tabs"
								operations={operations}
							>
								{activeTabId && (
									<Button
										variant="secondary"
										size="lg"
										className="mt-4 h-16 grow"
										onClick={() => setIsMoveToTabOpen(true)}
									>
										Move to Tab
									</Button>
								)}
							</CheckOperation>
						</CheckOperation>
					</ButtonGroup>
				)}

				{/* Close order (unpaid) — requires ManageTabs permission */}
				{openOrderData.status === "Open" &&
					openOrderData.outstanding &&
					openOrderData.outstanding.value > 0 && (
						<CheckOperation
							operation="staff-terminal-manage-tabs"
							operations={operations}
						>
							<Button
								variant="outline"
								size="lg"
								className="mt-2 h-14 w-full"
								onClick={handleCloseOrderClick}
							>
								Close Order
							</Button>
						</CheckOperation>
					)}
				{/* Dismiss order — no tab, no payments */}
				{openOrderData.status === "Open" &&
					!activeTabId &&
					!isFrozen &&
					openOrderData.payments.every((p) => p.status !== "Completed") && (
						<Button
							variant="outline"
							size="lg"
							className="mt-2 h-14 w-full"
							onClick={() => setIsDismissConfirmOpen(true)}
							disabled={isDismissing}
						>
							{isDismissing && <Loader2 className="mr-2 size-4 animate-spin" />}
							Dismiss Order
						</Button>
					)}
				{hasUnsentItems && !activeTabId && !isFrozen && (
					<Button
						variant="default"
						size="lg"
						className="mt-2 h-14 w-full"
						onClick={async () => {
							if (!currentOrder) return;
							try {
								const result = await sendToKitchen(currentOrder.id);
								// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- typed non-null, but a 200 with an empty body (the HA-570 failure) yields null; keep the guard
								const warnings = result?.response?.warnings ?? [];
								// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- typed non-null, but a 200 with an empty body (the HA-570 failure) yields null; keep the guard
								const refused = result?.response?.stockRejections ?? [];

								// HA-619 — the success toast is suppressed whenever anything
								// warned, so the operator is never told it printed and warned
								// that it didn't in the same breath.
								sendWarnings.report(warnings);

								// HA-588 AC13 — a refused line did not send; the rest of the
								// order did. The server's sentence says exactly that, so it is
								// shown as written rather than reworded here.
								setStockRejections(refused);
								for (const rejection of refused) {
									toast.error(rejection.message);
								}

								if (
									shouldShowSendSuccess(warnings) &&
									nothingWasRefused(refused)
								) {
									toast.success(SEND_SUCCESS);
								}
							} catch (error) {
								reportRefusal(error, "Failed to send to kitchen");
							}
						}}
					>
						Send to Kitchen
					</Button>
				)}

				{activeTabId && openOrderData.status === "Open" && !isFrozen && (
					<Button
						variant="default"
						size="lg"
						className="mt-2 h-14 w-full"
						disabled={isSaving}
						onClick={async () => {
							if (!currentOrder || isSaving) return;
							// HA-768 / D-HA-24 — leaves the order once the server has saved it; on a
							// failure the operator stays here with the order as it was.
							setIsSaving(true);
							try {
								await saveOrder(currentOrder.id);
							} catch (error) {
								reportRefusal(error, "Failed to save order");
							} finally {
								setIsSaving(false);
							}
						}}
					>
						{isSaving ? (
							<Loader2 className="mr-2 size-4 animate-spin" />
						) : (
							<Check className="mr-2 size-4" />
						)}
						Save
					</Button>
				)}

				<CheckOperation
					operation="staff-terminal-fire-courses"
					operations={operations}
				>
					{courseFire.pendingFire ? (
						<div className="flex gap-2 mt-2 w-full">
							<Button
								variant="destructive"
								size="lg"
								className="h-14 grow"
								onClick={courseFire.undoFire}
								disabled={courseFire.isCommitting}
							>
								<RotateCcw className="mr-2 size-4" />
								Undo ({courseFire.pendingFire.countdown}s)
							</Button>
							<Button
								variant="default"
								size="lg"
								className="h-14 grow"
								onClick={courseFire.confirmFire}
								disabled={courseFire.isCommitting}
							>
								<Check className="mr-2 size-4" />
								Confirm
							</Button>
						</div>
					) : (
						<Button
							variant="default"
							size="lg"
							className="mt-2 h-14 w-full"
							onClick={courseFire.fireNextCourse}
							disabled={
								!courseFire.hasUnfiredCourses ||
								courseFire.otherDevicePendingFire ||
								courseFire.isInitiating
							}
						>
							{courseFire.isInitiating ? (
								<Loader2 className="mr-2 size-4 animate-spin" />
							) : null}
							{courseFire.hasUnfiredCourses
								? courseFire.otherDevicePendingFire
									? "Firing..."
									: "Next Course Away"
								: "All Courses Sent"}
						</Button>
					)}
				</CheckOperation>

				{currentOrder && (
					<UnsentItemsDialog
						isOpen={isUnsentItemsDialogOpen}
						orderId={currentOrder.id}
						onClose={() => {
							setIsUnsentItemsDialogOpen(false);
							setPendingAction(null);
						}}
						onConfirmed={handleUnsentItemsRemoved}
					/>
				)}

				<CloseOrderDialog
					isOpen={isCloseOrderOpen}
					onOpenChange={setIsCloseOrderOpen}
					onConfirm={async (reason, notes) => {
						if (!currentOrder) return;
						try {
							await closeOrder(currentOrder.id, reason, notes);
						} catch (error) {
							toast.error("Failed to close order", {
								description:
									error instanceof Error ? error.message : "Unknown error",
							});
						}
					}}
				/>
				<AlertDialog
					open={isDismissConfirmOpen}
					onOpenChange={(open) => {
						// HA-764 — not while the dismiss is in flight; the dialog is its pending state.
						if (!isDismissing) setIsDismissConfirmOpen(open);
					}}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Dismiss this order?</AlertDialogTitle>
							<AlertDialogDescription>
								This cannot be undone.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel disabled={isDismissing}>
								Cancel
							</AlertDialogCancel>
							<AlertDialogAction
								disabled={isDismissing}
								onClick={async (event) => {
									// HA-764 — Radix closes the dialog on Action click unless prevented. Kept
									// open so its spinner shows while dismissOrder waits for the server; the
									// finally below closes it once the request has settled either way.
									if (!currentOrder) return;
									event.preventDefault();
									setIsDismissing(true);
									try {
										await dismissOrder(currentOrder.id);
									} catch (error) {
										toast.error("Failed to dismiss order", {
											description:
												error instanceof Error
													? error.message
													: "Unknown error",
										});
									} finally {
										setIsDismissing(false);
										setIsDismissConfirmOpen(false);
									}
								}}
							>
								{isDismissing && (
									<Loader2 className="mr-2 size-4 animate-spin" />
								)}
								Dismiss
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				<AddOrderToTabDialog
					isOpen={isAddToTabOpen}
					onOpenChange={(open) => {
						setIsAddToTabOpen(open);
						if (!open) setSelectedTabIdForOrder(null);
					}}
					tabs={tabs}
					selectedTabId={selectedTabIdForOrder}
					onSelectTab={setSelectedTabIdForOrder}
					onConfirm={attachOrderToTab}
					onCreateAndAssign={createTabAndAttachOrder}
					isCreating={isCreatingTabForOrder}
				/>
				<MoveOrderToTabDialog
					isOpen={isMoveToTabOpen}
					onOpenChange={setIsMoveToTabOpen}
					tabs={tabs}
					currentTabId={activeTabId || ""}
					onConfirm={handleMoveOrderToTab}
					isMoving={isMovingOrder}
				/>
				{openOrderData.status === "Paid" && (
					<Button
						variant="default"
						size="lg"
						className="mt-4 h-16 w-full"
						onClick={completeOrder}
						disabled={isCompleting}
					>
						{isCompleting ? (
							<Loader2 className="mr-2 size-5 animate-spin" />
						) : (
							<Check className="mr-2 size-5" />
						)}
						Complete
					</Button>
				)}
				{openOrderData.status === "Completed" && (
					<Button
						variant="default"
						size="lg"
						className="mt-4 h-16 w-full"
						onClick={goToStart}
					>
						<ArrowLeft className="mr-2 size-5" />
						Done
					</Button>
				)}
				{/* Back button for non-Open orders (from Order History) */}
				{(openOrderData.status === "Closed" ||
					openOrderData.status === "Refunded" ||
					openOrderData.status === "PartiallyRefunded") && (
					<Button
						variant="default"
						size="lg"
						className="mt-4 h-16 w-full"
						onClick={() => setView("products")}
					>
						<ArrowLeft className="mr-2 size-5" />
						Back
					</Button>
				)}
				{/* Refund button */}
				{(openOrderData.status === "Paid" ||
					openOrderData.status === "Completed" ||
					openOrderData.status === "Closed" ||
					openOrderData.status === "PartiallyRefunded") &&
					currentOrder && (
						<CheckOperation
							operation="staff-terminal-refund"
							operations={operations}
						>
							<Button
								variant="outline"
								size="lg"
								className="mt-2 h-14 w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
								onClick={() => {
									setRefundOrder(currentOrder);
									setIsRefundDialogOpen(true);
								}}
							>
								<RotateCcw className="mr-2 size-5" />
								Process Refund
							</Button>
						</CheckOperation>
					)}
			</CardFooter>
			<SendWarningDialog
				warnings={sendWarnings.blocking}
				onAcknowledge={sendWarnings.acknowledge}
			/>

			{/* HA-588 — only the operator knows whether the food came back, and the stock answer
			    differs either way, so the line is not removed until they say. */}
			<AlertDialog
				open={pendingRemoval !== null}
				// HA-265 (AC9): closing the prompt (Escape, a tap outside) keeps the line; only Yes or No removes it.
				onOpenChange={(open) => {
					if (!open) setPendingRemoval(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{RETURN_PROMPT_TITLE}</AlertDialogTitle>
						<AlertDialogDescription>
							{RETURN_PROMPT_BODY}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => confirmRemoval(false)}>
							No
						</AlertDialogCancel>
						<AlertDialogAction onClick={() => confirmRemoval(true)}>
							Yes
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
